import { useDroppable } from '@dnd-kit/core'
import { Button, Skeleton } from 'antd'
import { useEffect, useState } from 'react'
import { EstimateStatus, ProjectStatus, ProjectType } from '../../common/constants'
import { JobListFilterState } from '../JobList/useJobListFilters'
import { useJobColumn } from '../JobList/jobListApi'
import DraggableJobKanbanCard from './DraggableJobKanbanCard'
import { applyPendingMoves, columnDroppableId, movesConfirmedBy, PendingMove } from './jobKanbanDragModel'
import { compactDollars, KanbanColumn } from './jobKanbanModel'

export const COLUMN_PAGE_SIZE = 25

type Props = {
  column: KanbanColumn
  jobType?: ProjectType
  sold: boolean
  request: JobListFilterState['request']
  pendingMoves: PendingMove[]
  dragging: boolean
  isUndoTarget?: boolean
  condensed?: boolean
  onServerCaughtUp?: (jobId: number) => void
}

const JobKanbanColumn = ({
  column, jobType, sold, request, pendingMoves, dragging, isUndoTarget, condensed, onServerCaughtUp
}: Props) => {
  const [page, setPage] = useState(1)

  const filteredOut = sold
    ? !request.statuses.includes(column.status as ProjectStatus)
    : !request.estimateStatuses.includes(column.status as EstimateStatus)

  const query = useJobColumn({
    ...request,
    jobType,
    sold,
    statuses: sold ? [column.status as ProjectStatus] : [],
    estimateStatuses: sold ? [] : [column.status as EstimateStatus],
    page: 1,
    pageSize: COLUMN_PAGE_SIZE * page
  }, !filteredOut)

  const takesTheCard = column.acceptsDrops || !!isUndoTarget

  const { setNodeRef, isOver } = useDroppable({
    id: columnDroppableId(column.status),
    data: { status: column.status },
    /* A closed column stays a registered droppable. Disabling it stops dnd-kit reporting it
       as the drop target at all, so the board never learns which column was aimed at and
       cannot say why the move was refused. The refusal itself is decided in resolveDrop. */
    disabled: filteredOut
  })

  const loaded = filteredOut
    ? { jobs: [], totalRows: 0, totalFinalPrice: 0 }
    : {
      jobs: query.data?.jobs ?? [],
      totalRows: query.data?.totalRows ?? 0,
      totalFinalPrice: query.data?.totalFinalPrice ?? 0
    }

  const settled = query.isSuccess && !query.isFetching && !filteredOut
  const confirmed = settled ? movesConfirmedBy(column.status, loaded.jobs, pendingMoves) : []
  const confirmedKey = confirmed.join(',')

  useEffect(() => {
    confirmed.forEach(jobId => onServerCaughtUp?.(jobId))
  }, [confirmedKey])

  const { jobs, totalRows, totalFinalPrice } = applyPendingMoves(loaded, column.status, pendingMoves)
  const remaining = totalRows - jobs.length

  const classNames = ['job-kanban__column']
  if (isOver && takesTheCard) {
    classNames.push('job-kanban__column--over')
  }
  if (dragging && takesTheCard && !filteredOut) {
    classNames.push('job-kanban__column--open')
  }
  if (dragging && !takesTheCard) {
    classNames.push('job-kanban__column--closed')
  }

  return <div ref={setNodeRef} className={classNames.join(' ')} role="listitem" aria-label={column.label}>
    <header className="job-kanban__column-header" style={{ borderBlockStartColor: column.accent }}>
      <div className="job-kanban__column-title">
        <span className="job-kanban__dot" style={{ backgroundColor: column.accent }} />
        <span className="job-kanban__column-label">{column.label}</span>
        <span className="job-kanban__count">{totalRows}</span>
      </div>
      <div className="job-kanban__column-total">{compactDollars(totalFinalPrice)}</div>
    </header>

    <div className="job-kanban__column-body">
      {filteredOut && <p className="job-kanban__empty">Hidden by the status filter</p>}

      {!filteredOut && query.isLoading && <div className="job-kanban__loading">
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      </div>}

      {!filteredOut && !query.isLoading && jobs.length === 0 && <p className="job-kanban__empty">Nothing here</p>}

      {jobs.map(job => <DraggableJobKanbanCard
        key={job.id}
        job={job}
        accent={column.accent}
        from={column.status}
        condensed={condensed}
      />)}

      {remaining > 0 && <Button
        block
        type="text"
        size="small"
        className="job-kanban__load-more"
        loading={query.isFetching}
        onClick={() => setPage(p => p + 1)}
      >
        Load {Math.min(COLUMN_PAGE_SIZE, remaining)} more
      </Button>}
    </div>

    {dragging && !takesTheCard && column.closedReason && <div className="job-kanban__column-veil">
      <span className="job-kanban__column-veil-text">{column.closedReason}</span>
    </div>}

    {dragging && isUndoTarget && !column.acceptsDrops && <div className="job-kanban__column-veil">
      <span className="job-kanban__column-veil-text job-kanban__column-veil-text--undo">Drop to put it back</span>
    </div>}
  </div>
}

export default JobKanbanColumn
